import { cva, type VariantProps } from 'class-variance-authority';

// Centralized Tailwind class definitions for the KaSelect component family (mirrors
// `button-variants.ts`). Components compose these with `cnMerge(...)` so consumer-supplied classes
// (anchorClasses, triggerClasses, containerClasses, etc.) still override via tailwind-merge.
//
// Note: option highlight/selected styling is driven by reka's `data-[highlighted]` / `data-[state]`
// attributes (CSS state, not JS props), so those selectors live in the `base` string rather than as
// cva variants — only genuinely prop-driven branches become variants.

// Trigger shell (the role="combobox" element).
export const selectTriggerVariants = cva(
  'relative flex min-h-[36px] w-full min-w-[160px] cursor-pointer items-center gap-x-2 rounded bg-white px-3 py-1.5 text-left text-sm leading-6 shadow-sm ring-1 ring-inset focus:outline-none focus-visible:ring-2 dark:bg-navy-800 dark:text-gray-50',
  {
    variants: {
      invalid: {
        true: '!ring-red-500/60 focus-visible:!ring-red-500/80',
        false:
          'ring-gray-300 focus-visible:ring-blue-400 dark:ring-navy-600 dark:focus-visible:ring-blue-500',
      },
      disabled: {
        true: 'cursor-not-allowed opacity-50 focus-visible:ring-1 focus-visible:ring-gray-300 dark:focus-visible:ring-navy-600',
        false: '',
      },
      open: { true: '', false: '' },
    },
    compoundVariants: [
      // Highlight the trigger while the listbox is open, unless it's showing the invalid (red) ring.
      { open: true, invalid: false, class: 'ring-blue-400 dark:ring-blue-500' },
    ],
    defaultVariants: { invalid: false, disabled: false, open: false },
  },
);

// Single-select trigger text (the selected label or the placeholder).
export const selectSelectedTextVariants = cva('block w-full truncate text-left', {
  variants: {
    tone: {
      invalid: 'text-red-600 dark:text-red-400',
      selected: 'text-gray-900 dark:text-gray-50',
      placeholder: 'text-gray-600 dark:text-gray-400',
    },
  },
  defaultVariants: { tone: 'placeholder' },
});

// Listbox popover content (static).
export const selectContentVariants = cva(
  'mt-1 max-h-[40vh] w-[var(--reka-select-trigger-width)] min-w-[160px] max-w-[var(--reka-select-trigger-width)] overflow-hidden rounded bg-white text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-navy-900 dark:text-gray-50 dark:ring-navy-600 data-[state=closed]:animate-[scale-out_100ms_ease-in] data-[state=open]:animate-[scale-in_100ms_ease-out] origin-[var(--reka-select-content-transform-origin)]',
);

// Scrollable options viewport (static).
export const selectViewportVariants = cva(
  'relative h-full w-full flex-1 scroll-py-1 overflow-y-auto',
);

// Clear ("x") button (static).
export const selectClearButtonVariants = cva(
  'm-0 inline-flex items-center justify-center rounded p-0 text-gray-900 transition-colors hover:bg-black/5 focus:outline focus:outline-2 focus:outline-blue-400 dark:text-gray-50 dark:hover:bg-white/5',
);

// Option row (KaSelectOption). Highlight/selected come from data-* CSS selectors; the only
// prop-driven branch is the extra top padding when an option has both a leading icon and description.
export const selectOptionVariants = cva(
  'group relative m-0 w-full cursor-pointer select-none list-none border-b border-gray-300/30 py-2 pl-3 pr-2 last:border-b-0 focus:outline-none focus-visible:outline-none data-[disabled]:cursor-not-allowed data-[highlighted]:data-[disabled]:!bg-transparent data-[state=checked]:data-[highlighted]:bg-blue-300/35 data-[state=unchecked]:data-[highlighted]:bg-gray-300/30 dark:border-navy-600/60 dark:data-[highlighted]:data-[disabled]:!bg-transparent dark:data-[state=checked]:data-[highlighted]:bg-navy-500 dark:data-[state=unchecked]:data-[highlighted]:bg-navy-700 [&[data-state=checked]:not([data-highlighted])]:bg-blue-50 dark:[&[data-state=checked]:not([data-highlighted])]:bg-navy-600',
  {
    variants: {
      withLeadingPadding: { true: 'pt-1.5', false: '' },
    },
    defaultVariants: { withLeadingPadding: false },
  },
);

// Group wrapper (KaSelectGroup) — static.
export const selectGroupVariants = cva('border-b border-gray-300/30 dark:border-navy-600/60');

// Group label (KaSelectGroup): visually hidden when `hideLabel`, otherwise a sticky uppercase header.
export const selectGroupLabelVariants = cva('', {
  variants: {
    hideLabel: {
      true: 'sr-only',
      false:
        'sticky top-0 z-[1] block border-b border-gray-300/30 bg-white py-2 pl-3 pr-2 text-[10px] font-normal uppercase leading-[14px] text-gray-500 dark:border-navy-600/60 dark:bg-navy-900 dark:text-gray-400',
    },
  },
  defaultVariants: { hideLabel: false },
});

// Decorative left/right/arrow icon wrapper (KaSelectIconSlot) — static; aria-hidden is set in template.
export const selectIconSlotVariants = cva(
  'flex flex-shrink-0 flex-grow-0 select-none items-center justify-center [&>svg]:block [&>svg]:align-baseline',
);

export type KaSelectTriggerVariants = VariantProps<typeof selectTriggerVariants>;
export type KaSelectSelectedTextVariants = VariantProps<typeof selectSelectedTextVariants>;
